import React, { useContext, forwardRef } from "react";

import { Dropdown } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import profile from "../../../assets/images/profile/education/pic1.jpg";
import { ThemeContext } from "../../../context/ThemeContext";
import Logout from "../nav/Logout";
import ChangePassword from "../nav/ChangePassword";
import {
  appApi,
  useGetMyProjectsQuery,
  useGetAllProjectsForMatrixQuery,
} from "../../../store/appApi";
import {
  selectActiveProjectId,
  selectActiveProjectName,
  selectIsFormDirty,
  setActiveProject,
} from "../../../store/projectSlice";
import { selectUserRole } from "../../../store/authSlice";

// Custom toggle that accepts RB's onClick/ref but drops the injected dropdown-toggle class
const NoCaretToggle = forwardRef(({ children, onClick }, ref) => (
  <div ref={ref} onClick={onClick} style={{ cursor: "pointer" }}>
    {children}
  </div>
));
const Header = ({ onNote }) => {
  const { background, changeBackground } = useContext(ThemeContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const activeProjectId = useSelector(selectActiveProjectId);
  const activeProjectName = useSelector(selectActiveProjectName);
  const isDirty = useSelector(selectIsFormDirty);
  const userRole = useSelector(selectUserRole);
  const isSuperAdmin = userRole === "super-admin";
  const userInfo = useSelector((state) => state.auth.user?.name);
  const userRoleId = useSelector((state) => state.auth.user?.role);
  const { data: myProjectsData } = useGetMyProjectsQuery();
  const { data: allProjectsData } = useGetAllProjectsForMatrixQuery(undefined, {
    skip: !isSuperAdmin,
  });

  // Super-admins see every project in the switcher; staff see only their assigned ones
  const myProjects = isSuperAdmin
    ? (allProjectsData?.data ?? allProjectsData?.projects ?? [])
    : (myProjectsData?.projects ?? []);

  const handleProjectSwitch = (project) => {
    const id = project._id?.toString() ?? project.id?.toString();
    if (id === activeProjectId) return;

    const doSwitch = () => {
      dispatch(appApi.util.resetApiState());
      dispatch(
        setActiveProject({ id, name: project.projectName, city: project.city }),
      );
      navigate(`/dashboard/project/${id}`);
    };

    if (isDirty) {
      Swal.fire({
        icon: "warning",
        title: "Unsaved Changes",
        text: "You have unsaved changes. Switching projects will discard them. Proceed?",
        showCancelButton: true,
        confirmButtonText: "Switch Project",
        cancelButtonText: "Stay",
        confirmButtonColor: "#324336",
      }).then(({ isConfirmed }) => {
        if (isConfirmed) doSwitch();
      });
    } else {
      doSwitch();
    }
  };

  return (
    <div className="header">
      <div className="header-content">
        <nav className="navbar navbar-expand">
          <div className="collapse navbar-collapse justify-content-between">
            <div className="header-left d-flex align-items-center gap-3">
              {/* <div className="search_bar dropdown">
                <span
                  className="search_icon p-3 c-pointer"
                  data-bs-toggle="dropdown"
                >
                  <i className="mdi mdi-magnify" />
                </span>
                <div className="dropdown-menu p-0 m-0">
                  <form>
                    <input
                      className="form-control"
                      type="search"
                      placeholder="Search"
                      aria-label="Search"
                    />
                  </form>
                </div>
              </div> */}

              {/* ── Active project indicator + switcher (single element) ─────── */}
              <Dropdown>
                <Dropdown.Toggle
                  as={NoCaretToggle}
                  style={{ cursor: "pointer", marginTop: 10 }}
                >
                  <div
                    className="d-flex align-items-center gap-2 px-3 py-2 rounded"
                    style={
                      activeProjectName
                        ? {
                            background: "#324336",
                            // border: "1px solid #5548c8",
                            minWidth: 180,
                            maxWidth: 300,
                          }
                        : {
                            background: "#fff3cd",
                            border: "1px dashed #ffc107",
                            minWidth: 180,
                            maxWidth: 300,
                          }
                    }
                  >
                    <i
                      className="fas fa-building flex-shrink-0"
                      style={{
                        fontSize: 15,
                        color: activeProjectName ? "#fff" : "#856404",
                      }}
                    />
                    <div className="flex-grow-1 overflow-hidden">
                      {activeProjectName ? (
                        <>
                          <div
                            style={{
                              fontSize: 10,
                              color: "rgba(255,255,255,0.7)",
                              fontWeight: 500,
                              lineHeight: 1,
                              marginBottom: 3,
                              textTransform: "uppercase",
                              letterSpacing: 0.6,
                            }}
                          >
                            Active Project
                          </div>
                          <div
                            className="text-truncate"
                            title={activeProjectName}
                            style={{
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: 13,
                              lineHeight: 1.2,
                            }}
                          >
                            {activeProjectName}
                          </div>
                        </>
                      ) : (
                        <span
                          style={{
                            color: "#856404",
                            fontWeight: 600,
                            fontSize: 13,
                          }}
                        >
                          No project selected
                        </span>
                      )}
                    </div>
                    <i
                      className="ti-angle-down flex-shrink-0"
                      style={{
                        fontSize: 10,
                        color: activeProjectName
                          ? "rgba(255,255,255,0.7)"
                          : "#856404",
                      }}
                    />
                  </div>
                </Dropdown.Toggle>

                <Dropdown.Menu className="mt-2" style={{ minWidth: 260 }}>
                  <div
                    className="px-3 py-2 border-bottom"
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      color: "#888",
                    }}
                  >
                    {isSuperAdmin ? "All Projects" : "My Projects"}
                  </div>
                  {myProjects.length === 0 ? (
                    <div
                      className="px-3 py-2 text-muted"
                      style={{ fontSize: 13 }}
                    >
                      No projects assigned.
                    </div>
                  ) : (
                    myProjects.map((project) => {
                      const id =
                        project._id?.toString() ?? project.id?.toString();
                      const isActive = id === activeProjectId;
                      return (
                        <Dropdown.Item
                          key={id}
                          onClick={() => handleProjectSwitch(project)}
                          className="d-flex align-items-center gap-2 py-2"
                          style={{
                            fontWeight: isActive ? 700 : 400,
                            color: isActive ? "#324336" : "inherit",
                            background: isActive ? "#3243360d" : "transparent",
                          }}
                        >
                          <i
                            className="fas fa-building flex-shrink-0"
                            style={{
                              fontSize: 14,
                              color: isActive ? "#324336" : "#aaa",
                            }}
                          />
                          <div className="flex-grow-1 overflow-hidden">
                            <div
                              className="text-truncate"
                              style={{ fontSize: 13 }}
                            >
                              {project.projectName}
                            </div>
                            {project.city && (
                              <div style={{ fontSize: 11, color: "#aaa" }}>
                                {project.city}
                              </div>
                            )}
                          </div>
                          {isActive && (
                            <i
                              className="ti-check flex-shrink-0"
                              style={{ color: "#324336", fontSize: 12 }}
                            />
                          )}
                        </Dropdown.Item>
                      );
                    })
                  )}
                  <div className="border-top mt-1 pt-1">
                    <Dropdown.Item
                      as={Link}
                      to="/dashboard"
                      className="d-flex align-items-center gap-2 py-2"
                      style={{ fontSize: 13, color: "#324336" }}
                    >
                      <i
                        className="ti-layout-grid2-alt"
                        style={{ fontSize: 14 }}
                      />
                      All Projects
                    </Dropdown.Item>
                  </div>
                </Dropdown.Menu>
              </Dropdown>
            </div>

            <ul className="navbar-nav header-right">
              {/* ── User profile dropdown ─────────────────────────────────────── */}
              <Dropdown as="li" className="nav-item header-profile">
                <Dropdown.Toggle as={NoCaretToggle}>
                  <div
                    className="ms-2 d-none d-lg-block"
                    style={{
                      display: "block",
                      flexDirection: "column",
                      backgroundColor: "#324336",
                      borderRadius: "8px",
                      width: 110,
                      height: 42,
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 12,
                      paddingTop: 5,
                      textAlign: "center",
                    }}
                  >
                    <span className="text-white">{userInfo}</span>
                    <br />
                    <span
                      className="text-white"
                      style={{
                        fontSize: 10,
                        fontWeight: 400,
                        textTransform: "capitalize",
                      }}
                    >
                      {userRoleId}
                    </span>
                  </div>
                </Dropdown.Toggle>
                <Dropdown.Menu
                  align="end"
                  className="mt-3 dropdown-menu dropdown-menu-right"
                >
                  {/* <Link
                    to="/app-profile"
                    className="dropdown-item ai-icon icon-bell-effect"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    <span className="ms-2">Profile</span>
                  </Link>
                  <Link to="/email-inbox" className="dropdown-item ai-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    <span className="ms-2">Inbox</span>
                  </Link> */}
                  <ChangePassword />
                  <Logout />
                </Dropdown.Menu>
              </Dropdown>
            </ul>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default Header;
